<script lang="ts">
import IconSprite from "@/components/IconSprite.vue";
import ValidierungsFehler from "@/components/ValidierungsFehler.vue";
import { computed, defineComponent, inject, ref } from "vue";
import Logger from "js-logger";
import Modal from "@/components/Modal.vue";
import VorschlagDirekt from "@/components/vorschlag/modals/VorschlagDirekt.vue";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "AbschlussDirekt",
  components: {
    VorschlagDirekt,
    Modal,
    IconSprite,
    ValidierungsFehler,
  },
  props: {
    fehler: Object,
    produkt: String,
  },
  setup(props) {
    const piniaStore = useFormStore();
    const showError = ref(false);
    const showSuccess = ref(false);
    const istSichtbar: any = inject("istSichtbar");
    const wechsleSichtbarkeit: any = inject("wechsleSichtbarkeit");
    const sollAntragPerEmailSendenKoennen = process.env.VUE_APP_ANTRAG_PER_EMAIL === "true";

    const getAntragOderVertrag = computed(() => {
      if (!piniaStore.state.isMakler && !piniaStore.state.isDunkelverarbeitung) {
        return "Antrag";
      }
      return "Vertrag";
    });

    const isDunkelverarbeitung = computed(() => {
      Logger.debug("piniaStore.state.isDunkelverarbeitung", piniaStore.state.isDunkelverarbeitung);
      return piniaStore.state.isDunkelverarbeitung;
    });

    function angabenSichern() {
      wechsleSichtbarkeit("modal-vorschlagDirekt");
    }

    return {
      props,
      piniaStore,
      getAntragOderVertrag,
      isDunkelverarbeitung,
      angabenSichern,
      showSuccess,
      showError,
      wechsleSichtbarkeit,
      istSichtbar,
      sollAntragPerEmailSendenKoennen,
    };
  },
});
</script>

<template>
  <div v-if="!piniaStore.state.isMakler" class="o-grid">
    <div class="o-grid__cell o-grid__cell--12">
      <p class="u-mar-t-0">
        <strong>
          Sie erhalten hiermit Ihre
          {{ getAntragOderVertrag }}sunterlagen. Laden Sie bitte das nachfolgende PDF herunter, speichern dieses ab oder
          drucken es aus.
        </strong>
        <br />
        Ihre {{ getAntragOderVertrag }}sunterlagen enthalten: Das Informationsblatt zu Versicherungsprodukten, das
        Tarifinformationsblatt, die Kundeninformationen (einschließlich Belehrung &uuml;ber das Widerrufsrecht), die
        Hinweise zur vorvertraglichen Anzeigepflicht, die Versicherungsbedingungen, die Satzung und die
        Datenschutzhinweise zur Antrags- und Vertragsverarbeitung. Diese sind Grundlage und Bestandteil Ihres
        Direktabschlusses.
      </p>
      <br />
      <div class="c-form-control-checkbox form-group">
        <input
          type="checkbox"
          class="c-form-control-checkbox__input"
          id="erklaerungUnterlagenErhalten"
          name="erklaerungUnterlagenErhalten"
          required
          aria-required="true"
          data-fehlermeldung="Bitte bestätigen Sie die Erklärung."
          data-cy="erklaerungUnterlagenErhalten"
        />
        <label class="c-form-control-checkbox__label" for="erklaerungUnterlagenErhalten">
          <icon-sprite :ist-klein="true" css-klassen="c-form-control-checkbox__icon" icon-name="icon-check-mark" />
          <span class="c-form-control-checkbox__label-text"
            >Ja, ich habe die
            <a @click.prevent="$emit('vorschlag-herunterladen')"> {{ getAntragOderVertrag }}sunterlagen </a>
            zur Kenntnis genommen. Nach Abschluss erhalte ich die Unterlagen mit der Bestätigungs-E-Mail.</span
          >
        </label>
        <validierungs-fehler :fehler-text="fehler.erklaerungUnterlagenErhalten" />
      </div>
      <br />
      <div class="c-form-control-checkbox form-group">
        <input
          type="checkbox"
          class="c-form-control-checkbox__input"
          id="erklaerungZumProduktUndBeratungsverzicht"
          name="erklaerungZumProduktUndBeratungsverzicht"
          required
          aria-required="true"
          data-fehlermeldung="Bitte bestätigen Sie die Erklärung."
          data-cy="erklaerungZumProduktUndBeratungsverzicht"
        />
        <label class="c-form-control-checkbox__label" for="erklaerungZumProduktUndBeratungsverzicht">
          <icon-sprite :ist-klein="true" css-klassen="c-form-control-checkbox__icon" icon-name="icon-check-mark" />
          <span class="c-form-control-checkbox__label-text">
            <slot name="angaben-bestaetigen"></slot>
          </span>
        </label>
        <validierungs-fehler :fehler-text="fehler.erklaerungZumProduktUndBeratungsverzicht" />
      </div>
      <br />
      <div class="c-form-control-checkbox form-group">
        <input
          type="checkbox"
          class="c-form-control-checkbox__input"
          id="erklaerungWerbeeinwilligung"
          name="erklaerungWerbeeinwilligung"
          v-model="piniaStore.state.werbeeinwilligung"
          data-cy="erklaerungWerbeeinwilligung"
        />
        <label class="c-form-control-checkbox__label" for="erklaerungWerbeeinwilligung">
          <icon-sprite :ist-klein="true" css-klassen="c-form-control-checkbox__icon" icon-name="icon-check-mark" />
          <span class="c-form-control-checkbox__label-text"
            >Ich bin damit einverstanden, von der GEV per E-Mail oder per Telefon Informationen zu Produkten aus dem
            Bereich Versicherungen zu erhalten. Dieser Einwilligung kann ich jederzeit unter
            werbewiderspruch@grundvers.de widersprechen.</span
          >
        </label>
        <validierungs-fehler :fehler-text="fehler.erklaerungWerbeeinwilligung" />
      </div>
      <br />
      <div class="o-grid o-grid--align-left">
        <div class="o-grid__cell o-grid__cell--12-360 o-grid__cell--4-1024">
          <button
            id="btnSummarySubmit"
            class="c-button c-button--full-width c-button--word-wrap-flex-height u-mar-b-6"
            style="height: 3.5rem !important"
            @click="$emit('antrag-absenden')"
            data-cy="btnSummarySubmit"
          >
            {{ isDunkelverarbeitung ? "Jetzt sicher abschließen (beitragspflichtig)" : "Jetzt verbindlich beantragen" }}
          </button>
        </div>
        <div v-if="sollAntragPerEmailSendenKoennen" class="o-grid__cell o-grid__cell--12-360 o-grid__cell--4-1024">
          <button
            id="btnSummarySubmit"
            style="font-weight: normal"
            class="c-button c-button--inverted c-button--full-width c-button--word-wrap-flex-height u-mar-b-6"
            @click="angabenSichern"
            data-cy="btnSummarySubmit"
          >
            <b>Angaben sichern</b> <br />
            Wiedereinstiegs-Link per E-Mail
          </button>
        </div>
      </div>
    </div>
  </div>
  <modal
    v-if="istSichtbar('modal-vorschlagDirekt')"
    :text="'Lassen Sie sich einen Vorschlag per E-Mail zusenden. Die E-Mail enthält neben dem Vorschlag auch einen Link, über den Sie jederzeit in den Tarifrechner zurückkehren können.'"
    :titel="'Ihr Vorschlag per E-Mail'"
    @closeModal="wechsleSichtbarkeit('modal-vorschlagDirekt')"
  >
    <template #inhalt="inhaltProps">
      <vorschlag-direkt
        :antrag="false"
        :is-vorschlag-email="true"
        :ist-outbound-aktiv="piniaStore.state.outbound.emailAktiv"
        :produkt="produkt"
        trackingZusatz="abschluss"
        @closeModal="wechsleSichtbarkeit('modal-vorschlagDirekt')"
        @textVerbergen="inhaltProps.textVerbergenToggle"
      />
    </template>
  </modal>
</template>
